import { useMemo } from 'react';
import dayjs from 'dayjs';
import { AnimatePresence, motion } from 'motion/react';
import { i18next } from '@frontend/i18n';
import { BannerNotification } from '@frontend/design-system';

interface PastDateBannerProps {
  initialDate?: string;
  initialDateFormat?: string;
  currentDate?: string;
  currentDateFormat?: string;
  bannerMargin?: string;
  bannerMessage?: string;
}

const DEFAULT_BANNER_MESSAGE = i18next.t(
  'You have selected a past date. You can proceed, but please ensure this is intentional.',
  { ns: 'schedule' }
);

export const usePastDateBanner = ({
  initialDate,
  initialDateFormat = 'MM/DD/YYYY',
  currentDate,
  currentDateFormat = 'MM/DD/YYYY',
  bannerMargin = '0px',
  bannerMessage,
}: PastDateBannerProps) => {
  const shouldShowBanner = useMemo(() => {
    if (!currentDate) return false;

    // don't show banner if initialDate is before today
    if (!!initialDate && dayjs(initialDate, initialDateFormat).isBefore(dayjs(), 'day')) {
      return false;
    }

    // show banner if currentDate is before today
    return dayjs(currentDate, currentDateFormat).isBefore(dayjs(), 'day');
  }, [initialDate, initialDateFormat, currentDate, currentDateFormat]);

  const pastDateBannerComponent = (
    <AnimatePresence>
      {shouldShowBanner && (
        <motion.div
          css={{ overflow: 'hidden', flexShrink: 0 }}
          initial={{ opacity: 0, height: 0, margin: 0 }}
          animate={{ opacity: 1, height: 'auto', margin: bannerMargin }}
          exit={{ opacity: 0, height: 0, margin: 0 }}
          transition={{ duration: 0.5, ease: 'easeInOut' }}
        >
          <BannerNotification status='warn' message={bannerMessage || DEFAULT_BANNER_MESSAGE} />
        </motion.div>
      )}
    </AnimatePresence>
  );

  return { pastDateBannerComponent };
};
